import { Component, ViewChild, ElementRef, Input } from '@angular/core';
import { Router } from '@angular/router';

import { Subscription, take, timer } from 'rxjs';

import { TestService } from '../../services/test.service';
import { QuestionService } from '../../services/question.service';

import { TestComponent } from '../../components/test/test.component';
import { QuestionComponent } from '../../question/question/question.component';
import { ModalComponent } from '../../components/modal/modal.component';
import { Test } from '../../interfaces/testInterface';
import { QuestionOverviewComponent } from '../question-overview/question-overview.component';
import { NgIconComponent } from '@ng-icons/core';
import { TimerPipe } from '../../pipes/timer.pipe';
import { Chance } from 'chance';

@Component({
    selector: 'app-exam-execution',
    imports: [TestComponent, QuestionComponent, ModalComponent, TimerPipe, QuestionOverviewComponent, NgIconComponent],
    templateUrl: './exam-execution.component.html',
    styleUrl: './exam-execution.component.scss'
})
export class ExamExecutionComponent {
  @ViewChild('progressbar', { read: ElementRef })
  progressbar!: ElementRef<HTMLDivElement>;

  @ViewChild('questionOverviewModal')
  questionOverviewModal!: ModalComponent;

  @ViewChild(QuestionOverviewComponent)
  questionOverview!: QuestionOverviewComponent;

  @ViewChild('submitModal')
  submitModal!: ModalComponent;

  @ViewChild('abortModal')
  abortModal!: ModalComponent;

  @Input()
  set id(testId: string) {
    this.loadTest(testId);
  }

  public test!: Test;
  private startTime!: Date;
  private currentQuestionSelectedTime: Date = new Date();
  public currentQuestionIndex = 0;
  public answers: Map<string, { elapsedSeconds: number, answers: string[] }> = new Map();
  public currentAnswers: string[] = [];
  public remainingSeconds = 0;
  public flaggedQuestions: string[] = [];
  public canProceed = false;
  private timeSubscription: Subscription | undefined;
  
  public orderSeed: string = new Chance().string({length: 16});

  constructor(private testService: TestService, private questionService: QuestionService, private router: Router) { }

  ngOnDestroy(): void {
    if (this.timeSubscription) {
      this.timeSubscription.unsubscribe();
    }
  }

  async loadTest(testId: string) {
    const test = await this.testService.getOneTest(testId);
    const startTime = localStorage.getItem(`test-${testId}-startTime`);

    if (startTime === null) {
      this.startTime = new Date();
      localStorage.setItem(`test-${test.id}-startTime`, this.startTime.toISOString());
    } else {
      this.startTime = new Date(startTime);
      this.loadStateFromStorage(test);
    }

    this.test = test;

    this.timeSubscription = timer(0, 1000).subscribe(() => this.evaluateTime());

    this.flaggedQuestions = await this.questionService.getFlaggedQuestions(test.id);
  }

  /**
   * updates the progress bar and remaining time
   * automatically submits the test if time is up
   */
  evaluateTime() {
    const allowedDurationSeconds = this.test.expand.conditions.time_limit * 60;
    const currentTime = new Date();

    const elapsedSeconds = (currentTime.getTime() - this.startTime.getTime()) / 1000;

    let progress = 100 - (elapsedSeconds / allowedDurationSeconds) * 100;
    this.progressbar.nativeElement.style.width = `${progress}%`;

    this.remainingSeconds = Math.floor(allowedDurationSeconds - elapsedSeconds);

    if (this.remainingSeconds <= 0) {
      this.submit();
    } else if (this.remainingSeconds <= this.test.expand.conditions.time_limit * 60 * 10 / 100 && this.progressbar.nativeElement.classList.contains('bg-blue')) {
      this.progressbar.nativeElement.classList.remove('bg-blue');
      this.progressbar.nativeElement.classList.add('time-warning');
    }
  }

  loadStateFromStorage(test: Test) {
    const currentQuestionIndex = localStorage.getItem(`test-${test.id}-currentQuestionIndex`);
    if (currentQuestionIndex !== null) {
      this.currentQuestionIndex = parseInt(currentQuestionIndex);
    }

    const currentQuestionSelectedTime = localStorage.getItem(`test-${test.id}-currentQuestionSelectedTime`);
    if (currentQuestionSelectedTime !== null) {
      this.currentQuestionSelectedTime = new Date(currentQuestionSelectedTime);
    }

    const answers = localStorage.getItem(`test-${test.id}-answers`);
    if (answers !== null) {
      this.answers = new Map(JSON.parse(answers));
      this.currentAnswers = this.answers.get(test.expand.questions[this.currentQuestionIndex].id)?.answers || [];
    }
    this.evaluateCanProceed();
  }

  async setQuestionIndex(index: number) {
    this.addElapsedTime();

    this.currentQuestionIndex = index;
    this.currentAnswers = this.answers.get(this.test.expand.questions[this.currentQuestionIndex].id)?.answers || [];
    localStorage.setItem(`test-${this.test.id}-currentQuestionIndex`, this.currentQuestionIndex.toString());

    if (this.remainingSeconds > 0) {
      this.currentQuestionSelectedTime = new Date();
      localStorage.setItem(`test-${this.test.id}-currentQuestionSelectedTime`, this.currentQuestionSelectedTime.toISOString());
    }

    this.evaluateCanProceed();
  }

  evaluateCanProceed(){
    if(this.test){
      if(this.test.expand.questions[this.currentQuestionIndex].show_expected){
        const question = this.test.expand.questions[this.currentQuestionIndex];
        const correctCount = question.expand.answer_options?.filter((a) => a.correct).length || 0;
        this.canProceed = this.currentAnswers.length >= correctCount;
      } else {
        this.canProceed = this.currentAnswers.length > 0;
      }
    }
  }

  /**
   * Adds the selected answers to the *current* question
   */
  handleAnswersSelected(answers: string[]) {
    const mapEntry = this.answers.get(this.test.expand.questions[this.currentQuestionIndex].id) || { elapsedSeconds: 0, answers: [] };

    mapEntry.answers = answers;

    this.currentAnswers = answers;
    this.answers.set(this.test.expand.questions[this.currentQuestionIndex].id, mapEntry);
    localStorage.setItem(`test-${this.test.id}-answers`, JSON.stringify(Array.from(this.answers.entries())));

    this.evaluateCanProceed();
  }

  async handleQuestionFlagged(index: number, flagged: boolean) {
    this.handleQuestionFlaggedId({id: this.test.expand.questions[index].id, flagged})
  }

  async handleQuestionFlaggedId(params: {id: string, flagged: boolean}) {
    if (params.flagged) {
      this.flaggedQuestions = await this.questionService.flagQuestion(params.id, this.test.id);
    } else {
      this.flaggedQuestions = await this.questionService.unflagQuestion(params.id, this.test.id);
    }
  }

  isQuestionFlagged(index: number) {
    return this.flaggedQuestions.includes(this.test.expand.questions[index].id);
  }

  /**
   * Adds the elapsed time to the *current* question
   */
  addElapsedTime() {
    const mapEntry = this.answers.get(this.test.expand.questions[this.currentQuestionIndex].id) || { elapsedSeconds: 0, answers: [] };

    mapEntry.elapsedSeconds += (new Date().getTime() - this.currentQuestionSelectedTime.getTime()) / 1000;

    this.answers.set(this.test.expand.questions[this.currentQuestionIndex].id, mapEntry);
    localStorage.setItem(`test-${this.test.id}-answers`, JSON.stringify(Array.from(this.answers.entries())));
  }

  /**
   * Send all answers to the backend (sequentially)
   * Then redirects to result page
   * If no answers are passed in, it uses the curerently stored answers (this.answers)
   */
  async submit(answers: Map<string, { elapsedSeconds: number, answers: string[] }> = this.answers) {
    const submitStartTime = new Date();

    this.addElapsedTime();
    this.submitModal.toggleModal();

    if (this.timeSubscription) {
      this.timeSubscription.unsubscribe();
    }

    for (const [questionId, answer] of answers.entries()) {
      await this.questionService.submitTestAnswer(this.test.id, questionId, answer.answers, answer.elapsedSeconds, this.orderSeed);
    }

    const delay = 2500 - (new Date().getTime() - submitStartTime.getTime());

    if (delay > 0) {
      timer(delay)
        .pipe(take(1))
        .subscribe(() => {
          this.router.navigate([`/evaluation/${this.test.id}`]);
        });
    } else {
      this.router.navigate([`/evaluation/${this.test.id}`]);
    }
  }

  async abort() {
    const answersToSubmit: Map<string, { elapsedSeconds: number, answers: string[] }> = new Map();

    for (const question of this.test.expand.questions) {
      if (!this.answers.has(question.id)) {
        answersToSubmit.set(question.id, { elapsedSeconds: 0, answers: [] });
      } else {
        answersToSubmit.set(question.id, this.answers.get(question.id)!);
      }
    }

    if(this.abortModal.showModal){
      this.abortModal.toggleModal();
    }
    
    await this.submit(answersToSubmit);
  }

  handleAbortClicked() {
    this.abortModal.toggleModal();
  }

  public toggleQuestionOverview() {
    this.questionOverview.reset();
    this.questionOverviewModal.toggleModal();
  }
}
